/**
 * SimpleSearch component that supports both real-time and on-demand search modes.
 *
 * Modes:
 * 1. Real-time search: Use `onChange` prop - searches as user types
 * 2. On-demand search: Use `onSearch` prop - searches when user hits Enter or clicks search button
 *
 * Features:
 * - Enter key support for quick searching
 * - Clear button when input has content
 * - Search button (when using onSearch mode)
 * - Fully controlled or uncontrolled state management
 */

"use client";

import React, { useState, useEffect } from "react";
import { TextInput, Paper, ActionIcon, Group } from "@mantine/core";
import { TbSearch, TbX } from "react-icons/tb";

interface SimpleSearchProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  onSearch?: (value: string) => void;
  showSearchButton?: boolean;
}

export function SimpleSearch({
  value = "",
  onChange,
  placeholder = "Search...",
  onSearch,
  showSearchButton = true,
}: SimpleSearchProps) {
  const [inputValue, setInputValue] = useState(value);

  // Sync with external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSearch = () => {
    if (onSearch) {
      onSearch(inputValue);
    } else if (onChange) {
      onChange(inputValue);
    }
  };

  const handleClear = () => {
    setInputValue("");
    if (onSearch) {
      onSearch("");
    } else if (onChange) {
      onChange("");
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // If no onSearch prop is provided, use real-time search
    if (!onSearch && onChange) {
      onChange(newValue);
    }
  };

  const showClearButton = inputValue.length > 0;
  const useSearchButton = onSearch && showSearchButton;

  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs">
        <TextInput
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          leftSection={!useSearchButton ? <TbSearch size={16} /> : undefined}
          size="md"
          style={{ flex: 1 }}
        />

        {showClearButton && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <TbX size={16} />
          </ActionIcon>
        )}

        {useSearchButton && (
          <ActionIcon
            variant="filled"
            size="lg"
            onClick={handleSearch}
            aria-label="Search"
          >
            <TbSearch size={16} />
          </ActionIcon>
        )}
      </Group>
    </Paper>
  );
}
